/* Smooth out the jump to the product gallery when the variant image set changes.
 *
 * StarApps Variant Images reveals the new image set by calling, from its own
 * prestige8_change_images():
 *
 *     galleryMediaElement.scrollIntoView();
 *
 * Bare, with no options. That resolves to behavior:"auto" — an instant jump —
 * and it is the snap people notice when switching between rug shapes.
 *
 * We cannot change StarApps' code, so this wraps scrollIntoView and, for that
 * one call only, performs a duration-controlled eased scroll instead.
 *
 * The interception is deliberately narrow. It applies ONLY when both hold:
 *   - the call passes zero arguments (StarApps' signature; Prestige and its
 *     carousels always pass an options object, e.g. {block:"nearest"}), and
 *   - the target sits inside .product-gallery
 * Every other scrollIntoView on the page is passed straight through untouched,
 * so gallery carousel navigation and theme behaviour are unaffected.
 */

(function () {
  'use strict';

  var DURATION = 700; // ms — native smooth is ~300 and reads as snappy
  var SETTLED_THRESHOLD = 40; // px — already close enough, don't animate at all
  var GAP = 20; // px of breathing room below the header

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var frame = null;
  var cancelled = false;

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /* How much vertical space the sticky header will occupy once this scroll
     finishes, so the gallery does not land underneath it.
     
     Two traps here, both learned the hard way:
     
     1. --sticky-area-height is not a number. Prestige defines it as a calc()
        expression, e.g. "calc(0 * 0px + 1 * 1 * 60.56px)", so parseFloat()
        on it returns NaN and any offset built from it silently becomes 0.
        We resolve CSS lengths with a probe element instead, which handles
        calc() and any unit the theme might switch to later.
     
     2. Even resolved, --sticky-area-height describes the header's state RIGHT
        NOW: it collapses to 0 while the header is hidden. The header carries
        hide-on-scroll, so scrolling up reveals it — by the time we land it is
        back at full height and covers whatever we aimed at. So we reserve
        --header-height, the revealed height, not the current one. */
  function resolveCssLength(expression) {
    var probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;visibility:hidden;pointer-events:none;top:0;left:0;height:' + expression;
    document.body.appendChild(probe);
    var height = probe.getBoundingClientRect().height;
    probe.remove();
    return height;
  }

  function stickyOffset() {
    var height = resolveCssLength('var(--header-height, 0px)');

    if (!height) {
      var header = document.querySelector('.header');
      height = header ? header.offsetHeight : 0;
    }

    /* An announcement bar only steals space when it is itself pinned. */
    var announcement = document.querySelector('.announcement-bar');
    if (announcement) {
      var position = getComputedStyle(announcement).position;
      if (position === 'fixed' || position === 'sticky') {
        height += announcement.offsetHeight;
      }
    }

    return height;
  }

  function stopAnimation() {
    cancelled = true;
    if (frame) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  }

  function animateTo(targetY) {
    var startY = window.scrollY;
    var maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    var endY = Math.max(0, Math.min(targetY, maxY));
    var distance = endY - startY;

    if (Math.abs(distance) < SETTLED_THRESHOLD) return;

    stopAnimation();
    cancelled = false;

    /* If scroll-behavior:smooth is ever active on the root, each per-frame
       scrollTo would itself be animated and fight this loop. Force it off for
       the duration, and use behavior:"instant" per frame as a second guard. */
    var root = document.documentElement;
    var previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    /* Any deliberate input from the person outranks our animation. */
    var abort = function () { stopAnimation(); };
    var events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    events.forEach(function (name) {
      window.addEventListener(name, abort, { passive: true });
    });

    function cleanup() {
      root.style.scrollBehavior = previousBehavior;
      events.forEach(function (name) {
        window.removeEventListener(name, abort);
      });
      frame = null;
    }

    var startTime = null;

    function step(now) {
      if (cancelled) return cleanup();
      if (startTime === null) startTime = now;

      var progress = Math.min(1, (now - startTime) / DURATION);
      var y = startY + distance * easeInOutCubic(progress);

      try {
        window.scrollTo({ top: y, behavior: 'instant' });
      } catch (e) {
        window.scrollTo(0, y);
      }

      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        cleanup();
      }
    }

    frame = requestAnimationFrame(step);
  }

  var nativeScrollIntoView = Element.prototype.scrollIntoView;

  Element.prototype.scrollIntoView = function () {
    var isBareCall = arguments.length === 0;
    var inGallery = typeof this.closest === 'function' && this.closest('.product-gallery');

    if (!isBareCall || !inGallery || reduceMotion.matches) {
      return nativeScrollIntoView.apply(this, arguments);
    }

    var top = this.getBoundingClientRect().top + window.scrollY - stickyOffset() - GAP;
    animateTo(top);
  };
})();
